'use client';

import { useTranslation } from '@/app/components/translations/Translation.tsx';
import { useState } from 'react';
import { Tooltip } from '@lmn/design-system';
import { isFrance } from '@/app/common/components/Stars/utils.ts';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { clientAppLogImageError } from '@/app/common/utils/clientAppLogImageError.ts';
import { getTourOperatorLogoUrls } from '@/app/common/contentHubAssets.ts';
import { YourOperatorLogoContainer } from './styled.ts';

const TourOperatorLogoBadge = ({ code }: { code?: string }) => {
  const { localization } = usePageWrapperData();
  const { t } = useTranslation();
  const [attempt, setAttempt] = useState(0);
  const [hasError, setHasError] = useState<boolean>(false);
  const isFR = isFrance(localization);
  const disclaimer = t('vacation.oss.cards.to_disclaimer'); // it has only values in DE, EN, EN_US, FR
  const urls = getTourOperatorLogoUrls(isFR, code);
  const url = urls[attempt];
  const hasLogo = !!(!hasError && disclaimer && code && url);

  const handleError = async () => {
    // A logo can sit in either DAM folder, under either casing, so a miss retries the remaining
    // candidates before the badge gives up. Only the last miss is worth reporting.
    if (attempt + 1 < urls.length) {
      setAttempt(attempt + 1);
      return;
    }
    setHasError(true);
    // Reporting the miss costs a HEAD request per card that has no artwork, so the market teams
    // turn it on while they collect the gap and off again afterwards (MPH-1082). The code and
    // country travel with it: they are what a market team acts on, and reading them back out of
    // the url means knowing which folder each market asks for.
    if (!localization?.configValuesApp?.logMissingToLogos) return;
    await clientAppLogImageError('TO_LOGO:handleImageError', url, undefined, {
      toCode: code,
      country: localization?.country,
    });
  };

  // The card is server-rendered, so the browser can request the logo — and get its 404 — before
  // React has hydrated and attached onError. That miss would otherwise go unseen, leaving a broken
  // image and no fallback: an already-loaded image with no intrinsic width is one (MPH-618).
  const handleMount = (image: HTMLImageElement | null) => {
    if (image?.complete && image.naturalWidth === 0) handleError();
  };

  return hasLogo ? (
    <YourOperatorLogoContainer>
      <Tooltip content={disclaimer} placement="top">
        <img key={url} src={url} alt="" ref={handleMount} onError={handleError} />
      </Tooltip>
    </YourOperatorLogoContainer>
  ) : null;
};
export default TourOperatorLogoBadge;
